import React from 'react';
import PropTypes from 'prop-types';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PaginationControls = ({
  currentPage,
  totalPages,
  totalItems,
  limit,
  hasNextPage,
  hasPrevPage,
  onPageChange,
  onLimitChange,
  limitOptions = [10, 25, 50, 100],
  className,
  border = true
}) => {
  const scrollContainerRef = React.useRef(null);
  const isDragging = React.useRef(false);
  const startX = React.useRef(0);
  const scrollLeft = React.useRef(0);
  const dragDistance = React.useRef(0);

  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const activeBtn = container.querySelector('[data-active="true"]');
      if (activeBtn) {
        const containerWidth = container.offsetWidth;
        const btnOffsetLeft = activeBtn.offsetLeft;
        const btnWidth = activeBtn.offsetWidth;
        const targetScrollLeft = btnOffsetLeft - (containerWidth / 2) + (btnWidth / 2);
        container.scrollTo({
          left: targetScrollLeft,
          behavior: 'smooth'
        });
      }
    }
  }, [currentPage]);

  const handleMouseDown = (e) => {
    isDragging.current = true;
    dragDistance.current = 0;
    startX.current = e.clientX;
    scrollLeft.current = scrollContainerRef.current.scrollLeft;
  };

  const handleMouseLeave = () => {
    isDragging.current = false;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.clientX;
    const walk = (x - startX.current) * 1.5;
    dragDistance.current = Math.abs(x - startX.current);
    scrollContainerRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const handlePageClick = (e, page) => {
    if (dragDistance.current > 5) {
      e.preventDefault();
      return;
    }
    onPageChange(page);
  };

  if (totalItems === 0) return null;

  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    pages.push(i);
  }

  const containerClasses = className !== undefined
    ? className
    : (border
        ? "bg-white rounded-lg border border-gray-200 p-4 mt-6"
        : "bg-white px-6 py-4 border-t border-gray-200");

  return (
    <div className={containerClasses}>
      <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
        {/* Results Count and Limit Selector */}
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-700">
            Showing {((currentPage - 1) * limit + 1).toLocaleString('en-IN')} to {Math.min(currentPage * limit, totalItems).toLocaleString('en-IN')} of {totalItems.toLocaleString('en-IN')} results
          </div>
          {onLimitChange && (
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-700">Items per page:</label>
              <select
                value={limit}
                onChange={(e) => onLimitChange(Number(e.target.value))}
                className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent"
              >
                {limitOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!hasPrevPage || currentPage <= 1}
            aria-label="Previous page"
            className="flex items-center justify-center h-9 w-9 text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          {/* Page Numbers */}
          <div
            ref={scrollContainerRef}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            className="relative flex items-center space-x-1 overflow-x-auto no-scrollbar select-none cursor-grab active:cursor-grabbing max-w-[116px] sm:max-w-[196px] md:max-w-[276px] lg:max-w-[396px]"
            style={{
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            <style>{`
              .no-scrollbar::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            {pages.map((page) => (
              <button
                key={page}
                data-active={page === currentPage}
                onClick={(e) => handlePageClick(e, page)}
                className={`h-9 min-w-[36px] px-2 text-sm font-medium rounded-md transition-colors shrink-0 ${
                  page === currentPage
                    ? 'bg-slate-900 text-white'
                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!hasNextPage || currentPage >= totalPages}
            aria-label="Next page"
            className="flex items-center justify-center h-9 w-9 text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

PaginationControls.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  totalItems: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
  hasNextPage: PropTypes.bool,
  hasPrevPage: PropTypes.bool,
  onPageChange: PropTypes.func.isRequired,
  onLimitChange: PropTypes.func,
  limitOptions: PropTypes.arrayOf(PropTypes.number)
};

export default PaginationControls;

